import React, { CSSProperties, useEffect, useState } from "react";

import TimeDiff from "components/basic/timer/TimeDiff";
import useCopyDataset from "hooks/useCopyDataset";
import useTimerThreshold from "hooks/useTimerThreshold";
import { Tooltip } from "primereact/tooltip";
import { T } from "services/lang/LanguageService";
import { formatTime } from "utils/time-utils";
import { ISparkLineHandlerProps, ValueLabel } from "./SparkLineTypes";
import SparkLineDisconnectedLabel from "./SparkLineDisconnectedLabel";

type SparkLineLabelProps = ISparkLineHandlerProps & {
  valueLabel: ValueLabel;
  setValueLabel: React.Dispatch<React.SetStateAction<ValueLabel>>;
  mappedValue?: string | number;
};

const SparkLineLabel: React.FC<SparkLineLabelProps> = ({
  valueLabel,
  setValueLabel,
  mappedValue,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { isExpired, resetTimer } = useTimerThreshold(props.maxAge, props.lastValueTime?.getTime());
  useCopyDataset(isHovered, props.title, props.dataset);

  useEffect(() => {
    resetTimer(props.lastValueTime?.getTime());
  }, [props.lastValueTime, resetTimer]);

  useEffect(() => {
    if (isExpired) {
      setValueLabel(prev => ({
        ...prev,
        content: <SparkLineDisconnectedLabel isMultipleCharts={props.isMultipleCharts} />,
        textColor: "red",
      }));
    }
  }, [isExpired, setValueLabel]);

  const id = `sparkline_value_${(Math.random() + 1).toString(16).substring(8)}`;
  const minValue = Math.min(...props.dataArray).toFixed(props.decimalPlaces || 0);
  const maxValue = Math.max(...props.dataArray).toFixed(props.decimalPlaces || 0);
  const style = {
    fontSize: props.labelFontSize || "2.5rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    lineHeight: "2.5rem",
    marginTop: props.isMultipleCharts ? "0.5rem" : "1.5rem",
    paddingLeft: props.isMultipleCharts ? "" : "0.5rem",
    position: props.overlayLabel ? "absolute" : "inherit",
    top: props.vertical === "top" ? "0" : "auto",
    bottom: props.vertical === "bottom" ? "0" : "auto",
    left: props.horizontal === "left" ? "0" : "auto",
    right: props.horizontal === "right" ? "0" : "auto",
    background: valueLabel.bgColor,
    textWrap: "wrap",
    overflowWrap: "break-word",
    color: isExpired ? "red" : valueLabel.textColor,
    textAlign:
      props.isMultipleCharts && props.generalProperties?.valueAlign
        ? props.generalProperties?.valueAlign
        : "left",
  } as CSSProperties;

  return (
    <>
      <div
        id={`${id}`}
        className="ea-sparkline-value"
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!isExpired && mappedValue ? mappedValue : valueLabel.content}
      </div>

      <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
        {props.lastValueTime && (
          <div>
            <b>{T("word.spark-line.last-value-time")}:</b> {formatTime(props.lastValueTime)} (
            {`${T("word.age")}: `}
            <TimeDiff time={props.lastValueTime} />)
          </div>
        )}
        <div>
          <b>{`${T("word.min")} ${T("word.value").toLowerCase()}`}:</b> {minValue} {props.unit}
        </div>
        <div>
          <b>{`${T("word.max")} ${T("word.value").toLowerCase()}`}:</b> {maxValue} {props.unit}
        </div>
      </Tooltip>
    </>
  );
};

export default SparkLineLabel;
